import axios from 'axios';
import { apiUrl } from '@/env';
import {
  IUserProfile,
  IUserProfileUpdate,
  IUserProfileCreate,
  ILesson,
  IUserRef,
  IStatus,
  IContact,
  ISelectChoices,
  IUserFlow,
  ICalendarEvent,
  ILandingResponse,
  IEvent,
  ISubscriptionPackage,
  ILessonLight,
  IComment,
  ILessonsResponse,
  IBook,
  ISubscriptionsInfo,
  ISubscriptionPeriod,
  IPackagePricePeriod,
  IMenu, ILessonsCategory,
  IStructureUserResponse,
  IStructureUserDetails,
  IActionLogResponse,
  ICreateLead,
  IUpdateWorkTime,
  IPaymentMethod,
  ITagsGroup,
  IPartnerUserDetail,
  IUserContactResponse,
  IContactDonationRow,
  IContactTasksRow,
  ICartPayment,
} from './interfaces';
import {ICategory, IContactsCategoryCreate} from '@/interfaces/contacts';
import { IStaffDashboardStatistics, IStaffDashboardStatisticsByCountries, IStaffDashboardStatisticsByStaff, IUsersImportList } from './interfaces/staff';
import {
  ICreateNewLeadTask,
  ICreateUserTask,
  ITicketDetail,
  IUpdateUserTask,
  IUserTasksResponse,
  IUserTicketsResponse,
} from './interfaces/user_tasks';
import { IFooterLink } from './interfaces/pages';

function authHeaders(token: string) {
  return {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `JWT ${token}`,
    },
  };
}

function authUploadHeaders(token: string) {
  return {
    headers: {
      'Content-Type': 'multipart/form-data',
      'Authorization': `JWT ${token}`,
    },
  };
}

export const api = {
  async logInGetToken(username: string, password: string) {
    const params = new URLSearchParams();
    params.append('username', username);
    params.append('password', password);

    return axios.post(`${apiUrl}/api/accounts/login/`, params);
  },
  async twoFactorAuth(data: any) {
    return axios.post<{next_step?: string, valid?: string, token?: string, user: IUserProfile}>(
      `${apiUrl}/api/accounts/login/`, data,
    );
  },
  async logInByToken(authToken: string) {
    const params = new URLSearchParams();
    params.append('token', authToken);

    return axios.post(`${apiUrl}/api/token-auth-bot/`, params);
  },
  async registerGetToken(data: { name: string, email: string, password: string, phone: string, promo: string, country: string }) {
    return axios.post(`${apiUrl}/api/accounts/register/`, data);
  },
  async getMe(token: string) {
    return axios.get<IUserProfile>(`${apiUrl}/api/accounts/me/`, authHeaders(token));
  },
  async checkMenuAccess(token: string, data: {url: string}) {
    return axios.post<{exists: boolean, allow: boolean}>(`${apiUrl}/api/menu/check_access/`, data, authHeaders(token));
  },
  async getMainMenu(token: string) {
    return axios.get<IMenu[]>(`${apiUrl}/api/menu/main/`, authHeaders(token));
  },
  async getDropdownMenu(token: string) {
    return axios.get<IMenu[]>(`${apiUrl}/api/menu/dropdown/`, authHeaders(token));
  },
  async getAdminRegisterLink() {
    return axios.get<string>(`${apiUrl}/api/accounts/admin_reg_link/`);
  },
  async updateMe(token: string, data: IUserProfileUpdate) {
    return axios.put(`${apiUrl}/api/accounts/me/`, data, authHeaders(token));
  },
  async connectGoogleAuthenticator(token: string) {
    return axios.post(`${apiUrl}/api/accounts/google_auth/connect/`, {}, authHeaders(token));
  },
  async disconnectGoogleAuthenticator(token: string) {
    return axios.post<IUserProfile>(`${apiUrl}/api/accounts/google_auth/disconnect/`, {}, authHeaders(token));
  },
  async updateUserAvatar(token: string, data: any) {
    return axios.post<IUserProfile>(`${apiUrl}/api/accounts/me/`, data, authHeaders(token));
  },
  async getUsers(token: string) {
    return axios.get<IUserProfile[]>(`${apiUrl}/api/v1/users/`, authHeaders(token));
  },
  async updateUser(token: string, userId: number, data: IUserProfileUpdate) {
    return axios.put(`${apiUrl}/api/v1/users/${userId}`, data, authHeaders(token));
  },
  async createUser(token: string, data: IUserProfileCreate) {
    return axios.post(`${apiUrl}/api/v1/users/`, data, authHeaders(token));
  },
  async passwordRecovery(email: string) {
    return axios.post(`${apiUrl}/api/password_reset/`, { email });
  },
  async resetPassword(password: string, token: string) {
    return axios.post(`${apiUrl}/api/password_reset/confirm/`, {
      password,
      token,
    });
  },
  async setUserLanguage(token: string, data: {language: string}) {
    return axios.post(
        `${apiUrl}/api/accounts/set_language/`,
        data,
        authHeaders(token),
    );
  },
  async getNewBook(token: string) {
    return axios.get<IBook>(`${apiUrl}/api/books/new/`, authHeaders(token));
  },
  async getBooks(token: string) {
    return axios.get<IBook[]>(`${apiUrl}/api/books/list/`, authHeaders(token));
  },
  async getBook(token: string, id: string) {
    return axios.get<IBook>(`${apiUrl}/api/books/view/${id}/`, authHeaders(token));
  },
  async submitPurchaseBook(token: string, data: any) {
    return axios.post<ICartPayment>(`${apiUrl}/api/books/purchase/`, data, authHeaders(token));
  },
  async getLessonsCategories(token: string) {
    return axios.get<ILessonsCategory[]>(`${apiUrl}/api/navigator/categories/list/`, authHeaders(token));
  },
  async getFreeLessons(token: string) {
    return axios.get<ILessonLight[]>(`${apiUrl}/api/navigator/lesson/free/list/`, authHeaders(token));
  },
  async getLessons(token: string, data: any) {
    return axios.post<ILessonLight[]>(`${apiUrl}/api/navigator/lesson/list/`, data, authHeaders(token));
  },
  async getLesson(token: string, lessonId: string) {
    return axios.get<ILesson>(`${apiUrl}/api/navigator/lesson/view/${lessonId}/`, authHeaders(token));
  },
  async setViewLesson(token: string, lessonId: string, time: number) {
    return axios.post(`${apiUrl}/api/navigator/lesson/view/${lessonId}/`, { time }, authHeaders(token));
  },
  async getComments(token: string) {
    return axios.get<IComment[]>(`${apiUrl}/api/navigator/comments/`, authHeaders(token));
  },
  async getLessonComments(token: string, lessonId: string) {
    return axios.get<IComment[]>(`${apiUrl}/api/navigator/comments/${lessonId}/`, authHeaders(token));
  },
  async addComment(token: string, data: any) {
    return axios.post(`${apiUrl}/api/navigator/comments/`, data, authHeaders(token));
  },
  async getDashboardLessons(token: string) {
    return axios.get(`${apiUrl}/api/navigator/lessons/dashboard/`, authHeaders(token));
  },
  async getImportantNews(token: string) {
    return axios.get(`${apiUrl}/api/news/important/`, authHeaders(token));
  },
  async getNewsDetailed(token: string, newsId: string) {
    return axios.get(`${apiUrl}/api/news/view/${newsId}`, authHeaders(token));
  },
  async getNews(token: string) {
    return axios.get(`${apiUrl}/api/news/list`, authHeaders(token));
  },
  async getSubscriptionPeriods(token: string) {
    return axios.post<ISubscriptionPeriod[]>(
      `${apiUrl}/api/subscription/periods/`, {}, authHeaders(token),
    );
  },
  async getSubscriptionPackages(token: string, data: any) {
    return axios.post<IPackagePricePeriod[]>(
      `${apiUrl}/api/subscription/packages/`, data, authHeaders(token),
    );
  },
  async getPackagePaymentUrl(token: string, data: { package: number }) {
    return axios.post<ICartPayment>(`${apiUrl}/api/subscription/package_purchase/`, data, authHeaders(token));
  },
  async getSubscriptionInfo(token: string) {
    return axios.get<ISubscriptionsInfo>(`${apiUrl}/api/subscription/info/`, authHeaders(token));
  },
  async getSubscriptionToggleAutoPay(token: string, data: { action: string }) {
    return axios.post(`${apiUrl}/api/subscription/info/`, data, authHeaders(token));
  },
  async getDonationPaymentUrl(token: string, data: { amount: number, auto_pay: boolean }) {
    return axios.post(`${apiUrl}/api/payments/donation/`, data, authHeaders(token));
  },
  async getUserRefs(token: string) {
    return axios.get<IUserRef[]>(`${apiUrl}/api/team/get_children/`, authHeaders(token));
  },
  async getUserPkRefs(pk: string, token: string) {
    return axios.get<IUserRef[]>(`${apiUrl}/api/team/get_children/${pk}/`, authHeaders(token));
  },
  async getContactsStatuses(token: string) {
    return axios.get<IStatus[]>(`${apiUrl}/api/contacts/status/`, authHeaders(token));
  },
  async getContactsCategories(token: string) {
    return axios.get<ICategory[]>(`${apiUrl}/api/contacts/category/`, authHeaders(token));
  },
  async createContactsCategory(token: string, data: IContactsCategoryCreate) {
    return axios.post(`${apiUrl}/api/contacts/category/`, data, authHeaders(token));
  },
  async updateContactsCategory(token: string, categoryId: string, data: IContactsCategoryCreate) {
    return axios.put(`${apiUrl}/api/contacts/category/${categoryId}/`, data, authHeaders(token));
  },
  async deleteContactsCategory(token: string, categoryId: string) {
    return axios.delete(`${apiUrl}/api/contacts/category/${categoryId}/`, authHeaders(token));
  },
  async getContacts(token: string, searchText?: string, statusId?: number, categoryId?: number) {
    return axios.get<IContact[]>(`${apiUrl}/api/contacts/?search=${searchText ?? ''}&category=${categoryId ?? '0'}&status=${statusId ?? '0'}`, authHeaders(token));
  },
  async getSelectChoices(choicesUrl: string, token: string) {
    return axios.get<ISelectChoices>(`${apiUrl}/api${choicesUrl}`, authHeaders(token));
  },
  async addContact(data: any, token: string) {
    return axios.post(`${apiUrl}/api/contacts/`, data, authUploadHeaders(token));
  },
  async updateContact(contactId: number, data: any, token: string) {
    return axios.put(`${apiUrl}/api/contacts/${contactId}/`, data, authUploadHeaders(token));
  },
  async getFlows(token: string) {
    return axios.get<IUserFlow[]>(`${apiUrl}/api/user_flows/`, authHeaders(token));
  },
  async addFlow(data: any, token: string) {
    return axios.post(`${apiUrl}/api/user_flows/`, data, authUploadHeaders(token));
  },
  async updateFlow(id: number, data: any, token: string) {
    return axios.put(`${apiUrl}/api/user_flows/${id}/`, data, authUploadHeaders(token));
  },
  async deleteFlow(id: number, token: string) {
    return axios.delete(`${apiUrl}/api/user_flows/${id}/`, authHeaders(token));
  },
  async getEvents(token: string) {
    return axios.get<ICalendarEvent[]>(`${apiUrl}/api/events/nearest/`, authHeaders(token));
  },
  async getEvent(token: string, pk: string) {
    return axios.get<IEvent>(`${apiUrl}/api/events/view/${pk}/`, authHeaders(token));
  },
  async getLandingElements(token: string, pk: string) {
    return axios.get<ILandingResponse>(`${apiUrl}/api/user_flows/landing_edit/${pk}/`, authHeaders(token));
  },
  async deleteLandingElement(landingId: string, elementId: number, token: string) {
    return axios.delete(`${apiUrl}/api/user_flows/landing_edit/${landingId}/${elementId}/`, authHeaders(token));
  },
  async updateLandingElement(landingId: string, id: number, data: any, token: string) {
    return axios.put(`${apiUrl}/api/user_flows/landing_edit/${landingId}/${id}/`, data, authUploadHeaders(token));
  },
  async addLandingElement(landingId: string, data: any, token: string) {
    return axios.post(`${apiUrl}/api/user_flows/landing_edit/${landingId}/`, data, authUploadHeaders(token));
  },
  async checkLandingUniqueName(token: string, data: any) {
    return axios.post<{matches: boolean, error: string}>(
        `${apiUrl}/api/user_flows/landing_edit/check_unique_name/`, data, authHeaders(token),
    );
  },
  async validateUserEmail(data: {email:  string,}) {
    return axios.post<{valid: boolean}>(
        `${apiUrl}/api/landings/validate_email/`, data
    );
  },
  async validateUserPhone(data: {phone:  string, user: number | null}) {
    return axios.post<{valid: boolean}>(
        `${apiUrl}/api/landings/validate_phone/`, data
    );
  },
  async getStaffDashboardStatistics(token: string, data: {period?: string, view_user_id?: number}) {
    return axios.post<IStaffDashboardStatistics>(
        `${apiUrl}/api/accounts/dashboard/statistics/`, data, authHeaders(token),
    );
  },
  async getStaffDashboardStatisticsByCountries(token: string, data: {period?: string}) {
    return axios.post<IStaffDashboardStatisticsByCountries[]>(
        `${apiUrl}/api/accounts/dashboard/statistics_by_countries/`, data, authHeaders(token),
    );
  },
  async getStaffDashboardStatisticsByStaff(token: string, data: {period?: string, search?: string, group?: null | number}) {
    return axios.post<IStaffDashboardStatisticsByStaff[]>(
        `${apiUrl}/api/accounts/dashboard/staff_statistics/`, data, authHeaders(token),
    );
  },
  async getUserTasksList(
    token: string,
    data: {mode?: string, linker_user_id?: number, view_user_id?: any},
    rowQuery: string,
  ) {
    return axios.post<IUserTasksResponse>(
        `${apiUrl}/api/user_tasks/list/${rowQuery}`, data, authHeaders(token),
    );
  },
  async createUserTask(token: string, data: ICreateUserTask) {
    return axios.post(
        `${apiUrl}/api/user_tasks/create/`, data, authHeaders(token),
    );
  },
  async createNewLeadTask(token: string, data: ICreateNewLeadTask) {
    return axios.post<{task_id: number}>(
        `${apiUrl}/api/user_tasks/create_new_lead_task/`, data, authHeaders(token),
    );
  },
  async updateUserWorkTimes(token: string, data: IUpdateWorkTime[]) {
    return axios.post(
        `${apiUrl}/api/accounts/user/update_work_times/`, data, authHeaders(token),
    );
  },
  async updateUserTask(token: string, data: IUpdateUserTask) {
    return axios.post(
        `${apiUrl}/api/user_tasks/update/`, data, authHeaders(token),
    );
  },
  async getUsersToLink(token: string) {
    return axios.get(
        `${apiUrl}/api/user_tasks/get_users_to_link/`, authHeaders(token),
    );
  },
  async getTicketsChoices(token: string, rowQuery: string) {
    return axios.get(
        `${apiUrl}/api/tickets/tickets_choices/${rowQuery}`, authHeaders(token),
    );
  },
  async getMyStructureUsers(
    token: string, data: {search?: string, view_user_id?: any, group?: any, sort_by?: string}, rowQuery: string,
  ) {
    return axios.post<IUserContactResponse>(
        `${apiUrl}/api/team/get_my_structure_users/${rowQuery}`, data, authHeaders(token),
    );
  },
  async getGetContactDonations(token: string, data: {user_id: number}, rowQuery: string) {
    return axios.post<IContactDonationRow[]>(
      `${apiUrl}/api/team/contact/get_donations/${rowQuery}`, data, authHeaders(token),
    );
  },
  async getGetContactTasks(token: string, data: {user_id: number}) {
    return axios.post<IContactTasksRow[]>(
        `${apiUrl}/api/team/contact/get_completed_tasks/`, data, authHeaders(token),
    );
  },
  async getWaitingUsers(
    token: string, data: {search?: string}, rowQuery: string,
  ) {
    return axios.post<IStructureUserResponse>(
        `${apiUrl}/api/team/get_waiting_users/${rowQuery}`, data, authHeaders(token),
    );
  },
  async rejectResponsibleUser(token: string, data: {user_id: number}) {
    return axios.post(
        `${apiUrl}/api/team/reject_responsible_user/`, data, authHeaders(token),
    );
  },
  async getUserDetails(token: string, data: {user_id: number}) {
    return axios.post<IStructureUserDetails>(
        `${apiUrl}/api/accounts/user/details/`, data, authHeaders(token),
    );
  },
  async getUserActionLogs(token: string, data: {user_id: number}, rowQuery: string) {
    return axios.post<IActionLogResponse>(
        `${apiUrl}/api/accounts/user/action_logs/${rowQuery}`, data, authHeaders(token),
    );
  },
  async submitBlockUnblockConsultant(
    token: string, data: {user_id: number, action: string},
  ) {
    return axios.post(
        `${apiUrl}/api/accounts/user/block_unblock_user/`, data, authHeaders(token),
    );
  },
  async submitRemoveUser(token: string, data: {user_id: number}) {
    return axios.post(
        `${apiUrl}/api/accounts/user/remove_user/`, data, authHeaders(token),
    );
  },
  async submitConsultantSettings(
    token: string, data: {user_id: number, limit?: number, rating?: number},
  ) {
    return axios.post(
        `${apiUrl}/api/accounts/user/consultant_settings/`, data, authHeaders(token),
    );
  },
  async createLeadManually(token: string, data: ICreateLead) {
    return axios.post(
        `${apiUrl}/api/accounts/user/create_lead_user/`, data, authHeaders(token),
    );
  },
  async createPartnerManually(token: string, data: FormData) {
    return axios.post(
        `${apiUrl}/api/accounts/user/create_partner_user/`, data, authUploadHeaders(token),
    );
  },
  async getTagsGroupsFields(token: string) {
    return axios.get<ITagsGroup[]>(
        `${apiUrl}/api/accounts/tags_groups/`, authHeaders(token),
    );
  },
  async getPartnerDetail(token: string, data: {id: number}) {
    return axios.post<IPartnerUserDetail>(
        `${apiUrl}/api/accounts/user/get_partner_details/`, data, authHeaders(token),
    );
  },
  async submitManagerGiveAwayUser(token: string, data: {consultant_id: number, user_id: number}) {
    return axios.post(
        `${apiUrl}/api/accounts/manager/give_away_user/`, data, authHeaders(token),
    );
  },
  async submitGiveAwayUser(token: string, data: {consultant_id: number, user_id: number}) {
    return axios.post(
        `${apiUrl}/api/accounts/user/give_away_user/`, data, authHeaders(token),
    );
  },
  async updateLeadSettings(
    token: string, data: {user_id: number, tags?: number[], groups?: number[]},
  ) {
    return axios.post(
        `${apiUrl}/api/accounts/user/update_settings/`, data, authHeaders(token),
    );
  },
  async updateTicket(
    token: string, data: {ticket_id: number, tags?: number[], groups?: number[], status?: number},
  ) {
    return axios.post(
        `${apiUrl}/api/tickets/consultant/update_ticket/`, data, authHeaders(token),
    );
  },
  async submitUserDonation(token: string, data: any) {
    return axios.post(
        `${apiUrl}/api/payments/donation/`, data, authUploadHeaders(token),
    );
  },
  async submitManualDonation(token: string, data: any) {
    return axios.post(
        `${apiUrl}/api/payments/manual_donation/`, data, authUploadHeaders(token),
    );
  },
  async getAvailablePaymentMethods(token: string, data: {category?: number, country?: string}) {
    return axios.post<IPaymentMethod[]>(
        `${apiUrl}/api/payments/get_payment_methods/`, data, authHeaders(token),
    );
  },
  async getMyStructureContactsChoices(token: string, rowQuery: string = '') {
    return axios.get(
        `${apiUrl}/api/team/get_my_structure_users/choices/${rowQuery}`, authHeaders(token),
    );
  },
  async getResetCredsTokenForUser(token: string, userId: number) {
    return axios.get<{reset_link_token: string}>(
        `${apiUrl}/api/accounts/user/reset_credentials/?user_id=${userId}`, authHeaders(token),
    );
  },
  async checkResetCredsToken(data: {token: string}) {
    return axios.post(
        `${apiUrl}/api/accounts/user/reset_credentials/`, data,
    );
  },
  async submitResetCredsByToken(
    data: {token: string, email: string, password: string},
  ) {
    return axios.put(
        `${apiUrl}/api/accounts/user/reset_credentials/`, data,
    );
  },
  async getAllConfigs(token: string) {
    return axios.get<any>(`${apiUrl}/api/configs/get_all_configs/`);
  },
  async createTicket(token: string, data: {type: any, text: string}) {
    return axios.post(`${apiUrl}/api/tickets/create/`, data, authHeaders(token));
  },
  async sendMsgThroughChatbot(token: string, data: {user_id: any, text: string}) {
    return axios.post(
        `${apiUrl}/api/chatbot_telegram/send_msg_through_chatbot/`,
        data, authHeaders(token),
    );
  },
  async getUserTicketsList(token: string, data: { user_id?: number}, rowQuery: string) {
    return axios.post<IUserTicketsResponse>(
        `${apiUrl}/api/tickets/consultant/list/${rowQuery}`, data, authHeaders(token),
    );
  },
  async getTicketDetail(token: string, data: { ticket_id: number}) {
    return axios.post<ITicketDetail>(
        `${apiUrl}/api/tickets/consultant/detail/`, data, authHeaders(token),
    );
  },
  async rejectResponsibleTicket(token: string, data: {ticket_id: number}) {
    return axios.post(
        `${apiUrl}/api/tickets/consultant/reject_ticket/`, data, authHeaders(token),
    );
  },
  async createTicketByConsultant(
    token: string, data: {user: number, type: any, text: string, consultants_groups: number[]},
  ) {
    return axios.post(`${apiUrl}/api/tickets/consultant/create_ticket/`, data, authHeaders(token));
  },
  async getFooterlinks(token: string) {
    return axios.get<IFooterLink[]>(`${apiUrl}/api/pages/footer_links/`, authHeaders(token));
  },
  async getUsersImportsList(token: string) {
    return axios.post<IUsersImportList[]>(`${apiUrl}/api/accounts/import_users/list/`, {}, authHeaders(token));
  },
  async saveUsersImport(token: string, data: FormData) {
    return axios.post(`${apiUrl}/api/accounts/import_users/save/`, data, authUploadHeaders(token));
  },
  async startUsersImport(token: string, data: {import_id: number, create_users: boolean}) {
    return axios.post(`${apiUrl}/api/accounts/import_users/start/`, data, authHeaders(token));
  },
};
